import React from 'react';
import { motion } from 'framer-motion';
import { reveal } from '../utils/motion';
import { CaseStudyCard } from '../components/CaseStudyCard';
import { SiteHeader } from '../components/SiteHeader';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { DrawnUnderline } from '../components/DrawnUnderline';
import { caseStudies, heroMetrics } from '../data/caseStudies';
import { clientLogos } from '../data/clientLogos';
import { linkClass } from '../utils/linkClass';

// Dark grey → black, applied per line so it stays consistent across the underlined word.
const gradientText = 'bg-[linear-gradient(to_bottom,#4a4a4a,#000000)] bg-clip-text text-transparent [-webkit-box-decoration-break:clone] [box-decoration-break:clone]';
export function Home() {
  return <div className="relative min-h-screen w-full bg-white font-sans text-ink" data-id="element-141" data-mp="3kvtmbh3q5vz0">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[760px] min-[700px]:h-[900px]" style={{
      backgroundImage: 'radial-gradient(rgba(0, 0, 0, 0.14) 1px, transparent 1.5px)',
      backgroundSize: '32px 32px',
      maskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(to bottom, #000 55%, transparent 100%)'
    }} data-id="element-142" data-mp="3owvlxz1k0a1i" />
      <div className="relative mx-auto flex max-w-[1347px] flex-col gap-[30px] px-5 py-10 md:px-10" data-id="element-143" data-mp="2mwqq76jix02p">
        <SiteHeader data-id="element-144" data-mp="2hz8fpbpuycsl" />

        <main className="flex flex-col gap-5 min-[700px]:gap-[30px]" data-id="element-145" data-mp="2hzqg8kjjzd6b">
          <section className="flex flex-col items-center gap-6 py-2 text-center min-[700px]:py-6 md:py-10" data-id="element-146" data-mp="2d5w1zwmpvqem">
            <motion.img src="https://cdn.magicpatterns.com/uploads/oThdsUnkXC7FRqpUL19do3/Meikson-Square.jpg" alt="Portrait of Michael Meikson" className="aspect-square w-[100px] shrink-0 rounded-full object-cover" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={reveal} data-id="element-147" data-mp="2861b3t2xz0tk" />
            <div className="flex max-w-[1100px] flex-col items-center gap-5" data-id="element-148" data-mp="1kmtjuic735s6">
              <motion.div className="flex flex-col gap-3" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              ...reveal,
              delay: 0.05
            }} data-id="element-149" data-mp="1onvlf5tl3txb">
                <h1 className="text-headline font-semibold text-black min-[700px]:text-[4rem]" data-id="element-150" data-mp="1fngy60x284r6">
                  <span className={gradientText} data-id="element-151" data-mp="23dkudn7x5285">Design that moves business </span>
                  <DrawnUnderline delay={2} strokeClassName="-bottom-[0.24em] h-[0.34em]" textClassName={gradientText} data-id="element-152" data-mp="28ce73dfdsp79">
                    forward
                  </DrawnUnderline>
                  <span className={gradientText} data-id="element-153" data-mp="2m91g4eklz5tj">.</span>
                </h1>
                <p className="text-subhead text-muted" data-id="element-154" data-mp="2r1b1rieoxjq0">
                  18 years of experience across B2B SaaS, fintech, and healthcare.
                </p>
              </motion.div>
            </div>
          </section>

          <motion.section aria-label="Clients" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          ...reveal,
          delay: 0.15
        }} data-id="element-155" data-mp="alo7vgb0k5wk">
            <ul className="grid grid-cols-3 items-center gap-x-4 gap-y-0 min-[700px]:gap-y-2 md:grid-cols-6" data-id="element-156" data-mp="kdlioly40vur">
              {clientLogos.map(logo => <li key={logo.name} className="flex items-center justify-center" data-id="element-157" data-mp="3nt93n9j1w0og">
                  <img src={logo.src} alt={logo.name} className="block aspect-[2/1] w-full max-w-[200px] object-contain" data-id="element-158" data-mp="3nvc5cqtkfi90" />
                </li>)}
            </ul>
          </motion.section>

          <ul aria-label="Results" className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-[30px]" data-id="element-159" data-mp="qnwmidhi9pj">
            {heroMetrics.map((m, i) => <motion.li key={m.label} className="flex flex-col rounded-[30px] bg-positive-bg p-5 [container-type:inline-size]" initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...reveal,
            delay: 0.3 + i * 0.06
          }} data-id="element-160" data-mp="1c4hz1wjdpims" data-mpi={i}>
                <span className="whitespace-nowrap text-[clamp(22px,21cqw,40px)] font-normal leading-[1.35] tracking-[-0.02em] text-positive max-[699px]:text-[28px]" data-id="element-161" data-mp="2harkvbvllle9">
                  {m.value}
                </span>
                <span className="text-xl leading-[27px] tracking-[-0.02em] max-[699px]:text-base max-[699px]:leading-6" data-id="element-162" data-mp="2qawn77lxptlk">
                  {m.label}
                </span>
              </motion.li>)}
          </ul>

          <section aria-label="Case studies" className="flex flex-col gap-[30px]" data-id="element-163" data-mp="3526mt3jgq0oz">
            {caseStudies.map(study => <CaseStudyCard key={study.id} study={study} animate data-id="element-164" data-mp="3a21e3875gsak" />)}
          </section>

          <section className="flex flex-col gap-[30px] pt-2" data-id="element-165" data-mp="3e34vhz4uiwdw">
            <div className="flex flex-col gap-3" data-id="element-166" data-mp="12fhsz136b35f">
              <h2 className="text-headline font-semibold" data-id="element-167" data-mp="12gb2uaxfi8rs">
                AI has become the core of my design practice.
              </h2>
              <p className="text-subhead text-muted" data-id="element-168" data-mp="te1thodwclfz">
                I prototype in code and can ship working software.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-[30px] md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]" data-id="element-169" data-mp="oh1nfdh1riov">
              <div className="flex flex-col gap-10" data-id="element-170" data-mp="1m2614fg138lb">
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-171" data-mp="1h3cosqch9nmq">
                <p data-id="element-172" data-mp="1cd5zeoa55z3f">
                  My primary design tool is Magic Patterns, where I maintain working prototypes of client projects. 
                </p>
                <a href="https://project-unified-inbox-with-filtering-117.magicpatterns.app/" target="_blank" rel="noopener noreferrer" className={`w-fit text-base ${linkClass}`} data-id="element-173" data-mp="17djmyh250mvo">
                  View RSI Prototype →
                </a>
              </div>
              <div className="flex flex-col gap-4 text-base leading-[1.6]" data-id="element-174" data-mp="26jt3ofl2mt0">
                <p data-id="element-175" data-mp="28wbg2p96z6l">
                  I built Percelo, an AEO (Answer Engine Optimization), tool solo in about two weeks with Claude Code. 
                </p>
                <a href="https://www.percelo.com" target="_blank" rel="noopener noreferrer" className={`w-fit text-base ${linkClass}`} data-id="element-176" data-mp="3piuau1oe3wiw">
                  Try Percelo →
                </a>
              </div>
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/8x9mJt8jkvyUyDgp9GG3CK/Percelo-hero2.png" alt="Percelo AI visibility report open on a laptop" className="h-[325px] w-full self-start rounded-[30px] object-cover object-[50%_45%]" data-id="element-177" data-mp="31nhshw5c13rz" />
            </div>
          </section>

          <TestimonialsSection data-id="element-178" data-mp="1exbu1hq9yqze" />
        </main>

        <div className="pt-10" data-id="element-179" data-mp="1eyd35gnqd3eh">
          <SiteHeader as="footer" data-id="element-180" data-mp="1ev7fm5ur29kv" />
        </div>
      </div>
    </div>;
}